import countries from '@/config/countries.json'
import type { ClientData } from '@/models/client.js'
import { formatDateToRelativeTime } from '@/utils/date/conversion.js'
import { useCurrentLocale } from '@/composables/i18next.js'

const currentLocale = useCurrentLocale()

const relativeTimeOptions: Intl.RelativeTimeFormatOptions = { numeric: 'auto' }
const relativeTimeFormatter = computed(
  () => new Intl.RelativeTimeFormat(currentLocale.value, relativeTimeOptions),
)

const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}
const dateFormatter = computed(
  () => new Intl.DateTimeFormat(currentLocale.value, dateOptions),
)

const timeOptions: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
}
const timeFormatter = computed(
  () => new Intl.DateTimeFormat(currentLocale.value, timeOptions),
)

const dateTimeOptions: Intl.DateTimeFormatOptions = {
  ...dateOptions,
  ...timeOptions,
}
const dateTimeFormatter = computed(
  () => new Intl.DateTimeFormat(currentLocale.value, dateTimeOptions),
)

export function useRelativeTimeFormat() {
  return {
    format: (date: Date) =>
      formatDateToRelativeTime(relativeTimeFormatter.value, date),
  }
}

export function useDateTimeFormat() {
  return {
    date: (date: Date) => dateFormatter.value.format(date),
    time: (date: Date) => timeFormatter.value.format(date),
    dateTime: (date: Date) => dateTimeFormatter.value.format(date),
  }
}

/**
 * Creates a formatter to consistently translate regions
 */
const regionNameFormatter = computed(
  () => new Intl.DisplayNames([currentLocale.value], { type: 'region' }),
)

export function useDisplayNames() {
  const INVALID_COUNTRY_MESSAGE = 'Invalid or unsupported country'

  return {
    regionName: (countryCode: string) =>
      countryCode
        ? (regionNameFormatter.value.of(countryCode) ?? INVALID_COUNTRY_MESSAGE)
        : INVALID_COUNTRY_MESSAGE,
  }
}

export type CountriesList = {
  code: ClientData['country']
  name: string
}[]
export function useCountriesList() {
  const countriesList = countries as CountriesList

  return countriesList.map((country) => {
    return {
      label: regionNameFormatter.value.of(country.code || ''),
      value: country.code,
    }
  })
}
